import { createInertiaApp, router } from '@inertiajs/vue3'
import { createApp, h } from 'vue'
import { createPinia } from 'pinia'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import axios from 'axios'
import VueAxios from 'vue-axios'
import { createHead } from '@unhead/vue'
import AppIcon from '~concierge/components/AppIcon.vue'
import Popper from 'vue3-popper'
import '~concierge/libs/fontawesome.js'
import '~concierge/styles/_variables.scss'
import Analytics from 'analytics'
import googleTagManager from '@analytics/google-tag-manager'
import { originalSourcePlugin } from '@analytics/original-source-plugin'
import * as Sentry from '@sentry/vue'
import { i18nVue } from 'laravel-vue-i18n'
import { VueReCaptcha } from 'vue-recaptcha-v3'

// Uses https://inertiajs.com/events
function inertiaRoutingInstrumentation(
    customStartTransaction,
    startTransactionOnPageLoad = true,
    startTransactionOnLocationChange = true
) {
    let activeTransaction
    let name
    if (startTransactionOnPageLoad) {
        name = route().current()
        activeTransaction = customStartTransaction({
            name,
            op: 'pageload',
            metadata: {
                source: 'route'
            }
        })
    }

    if (startTransactionOnLocationChange) {
        router.on('before', (_to, _from) => {
            if (activeTransaction) {
                activeTransaction.finish()
            }

            const newName = route().current()

            if (newName !== name) {
                activeTransaction = customStartTransaction({
                    name: newName,
                    op: 'navigation',
                    metadata: {
                        source: 'route'
                    }
                })
            }
        })

        router.on('finish', () => {
            activeTransaction.setName(route().current(), 'route')
        })
    }
}

axios.interceptors.request.use((config) => {
    const lang = document?.documentElement?.lang;
    if (lang) {
        config.headers['Accept-Content-Language'] = lang;
        config.headers['Accept-Language'] = lang;
    }
    config.headers['Original-Source'] = localStorage.getItem('__user_original_source');

    return config;
});

const pinia = createPinia()
const head = createHead()

createInertiaApp({
    resolve: name =>
        resolvePageComponent(`./Concierge/${name}.vue`, import.meta.glob('./Concierge/**/*.vue'), {
            eager: true
        }),
    setup({ el, App, props, plugin }) {
        const app = createApp({
            render: () => h(App, props)
        })
        const captchaKey = props.initialPage.props.recaptcha_site_key;
        Sentry.init({
            app,
            dsn: __CONCIERGE_SENTRY_PUBLIC_DSN__,
            environment: __APP_ENV__,
            release: __APP_VERSION__,
            integrations: [
                Sentry.browserTracingIntegration({
                    routingInstrumentation: inertiaRoutingInstrumentation
                }),

                Sentry.replayIntegration()
            ],

            trackComponents: true,

            // Set tracesSampleRate to 1.0 to capture 100%
            // of transactions for performance monitoring.
            // We recommend adjusting this value in production
            tracesSampleRate: 0.2,

            // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
            tracePropagationTargets: ['localhost', /^\/api\/alfred\//],

            // Capture Replay for 10% of all sessions,
            // plus for 100% of sessions with an error
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0
        })
        app
            .use(plugin)
            .component('Popper', Popper)
            .component('AppIcon', AppIcon)
            .use(VueAxios, axios)
            .use(pinia)
            .use(head)
            .use(i18nVue, {
                resolve: lang => {
                    return props.initialPage.props.languages[lang]
                },
                fallbackLang: document.documentElement.lang === 'en' ? 'fr' : 'en',
                fallbackMissingTranslations: true
            })
            .use(VueReCaptcha, { siteKey: captchaKey })

        app.config.globalProperties.$route = route
        app.mount(el)
    }
})
if (getGtmValues()) {
    var gtmContainers = {}
    getGtmValues().forEach(containerId => {
        const uniqueId = containerId.replace('GTM-', '')
        gtmContainers[uniqueId] = Analytics({
            app: 'Alfred',
            version: __APP_VERSION__,
            debug: true,
            plugins: [
                googleTagManager({
                    containerId
                }),
                originalSourcePlugin(),
            ]
        })

        router.on('navigate', event => {
            const pageDetails = {
                url: event.detail.page.url,
                title: event.detail.page.props?.analyticsRouteTitle ?? route().current(),
            }
            gtmContainers[uniqueId].page(pageDetails)
        })
    })
}

function getGtmValues() {
    const metaTags = document.getElementsByTagName('meta')
    const gtms = []
    for (const element of metaTags) {
        const metaTag = element
        if (metaTag.getAttribute('name') === 'gtm') {
            gtms.push(metaTag.getAttribute('content'))
        }
    }
    return gtms
}
